// The command envelope (docs/clairvoyance/04-commands.md §4.1).
// Clients create the envelope; the server sets SERVER_FIELDS. Firestore rules mirror this file.
import { z } from 'zod';
import { COUNTER_COMMANDS } from './commands/counter';
import {
  MOVEMENT_COMMANDS,
  reverseTestIntercompanyInvoice,
  shiftTestRun,
} from './commands/movement';
import { PARTNER_COMMANDS } from './commands/partners';
import { FINALMILE_COMMANDS } from './commands/finalmile';
import { CUSTOMER_COMMANDS } from './commands/customer';
import { INVOICING_COMMANDS } from './invoicing';
import { FINANCE_COMMANDS } from './finance';
import { FINANCE_CORE_COMMANDS } from './financeCore';
import { CHANGE_COMMANDS } from './commands/changes';
import { HELP_COMMANDS } from './commands/help';
import { defineCommand, type CommandDefinition } from './define';

const ULID = '[0-9A-HJKMNP-TV-Z]{26}';

export const commandIdSchema = z.string().regex(new RegExp(`^cmd_${ULID}$`), 'Invalid command id');
export const deviceIdSchema = z.string().regex(new RegExp(`^dev_${ULID}$`), 'Invalid device id');
export const codeSchema = z.string().regex(/^[A-Z0-9]{2,8}$/, 'Invalid code');
export const commandTypeSchema = z.string().regex(/^[a-z][A-Za-z]{1,47}$/, 'Invalid command type');

export const COMMAND_STATUSES = [
  'queued',
  'awaiting_approval',
  'applied',
  'applied_with_exception',
  'rejected',
] as const;
export type CommandStatus = (typeof COMMAND_STATUSES)[number];

/** Fields only the server may write. A client create that sets any of them is refused by the rules. */
export const SERVER_FIELDS = [
  'status',
  'principal',
  'receivedAt',
  'appliedAt',
  'result',
  'error',
  'exceptionIds',
  /** Set by `decideApproval` when a person released a command an agent wrote (21 §21.1). */
  'checkerApproval',
] as const;

export const MAX_PAYLOAD_KEYS = 40;
export const MAX_BARCODES_PER_COMMAND = 200;

/** What a client writes to `commands/{id}`. */
export const clientCommandSchema = z
  .object({
    id: commandIdSchema,
    type: commandTypeSchema,
    actorUid: z.string().min(1).max(128),
    deviceId: deviceIdSchema,
    branchId: codeSchema,
    occurredAt: z.iso.datetime({ offset: true }),
    clockOffsetMs: z
      .number()
      .int()
      .min(-7 * 24 * 3600 * 1000)
      .max(7 * 24 * 3600 * 1000),
    appVersion: z.string().min(1).max(40),
    payload: z.record(z.string(), z.unknown()),
  })
  .strict();

export type ClientCommand = z.infer<typeof clientCommandSchema>;

export interface CommandError {
  code: string;
  message: string;
}

/** The full stored command, after the server has seen it. */
export interface StoredCommand extends ClientCommand {
  status?: CommandStatus;
  principal?: string;
  receivedAt?: unknown;
  appliedAt?: unknown;
  result?: Record<string, unknown>;
  error?: CommandError;
  exceptionIds?: string[];
}

export { defineCommand, type CommandDefinition } from './define';

// ---- Phase 0 commands ------------------------------------------------------

/** Proves the pipeline end to end: offline write, sync, apply once, result. */
export const devEcho = defineCommand({
  type: 'devEcho',
  permission: 'dev.tools',
  principals: [],
  offline: true,
  devOnly: true,
  payload: z.object({ message: z.string().min(1).max(200) }).strict(),
});

export const COMMANDS = {
  devEcho,
  // A development tool (devOnly): undoes a TEST container's intercompany invoice on staging.
  reverseTestIntercompanyInvoice,
  // A development tool (devOnly): moves a TEST run's timeline back on staging.
  shiftTestRun,
  ...COUNTER_COMMANDS,
  ...MOVEMENT_COMMANDS,
  ...PARTNER_COMMANDS,
  ...FINALMILE_COMMANDS,
  ...CUSTOMER_COMMANDS,
  ...INVOICING_COMMANDS,
  ...FINANCE_COMMANDS,
  ...FINANCE_CORE_COMMANDS,
  ...CHANGE_COMMANDS,
  ...HELP_COMMANDS,
} as const;
export type CommandType = keyof typeof COMMANDS;
export type PayloadOf<T extends CommandType> = z.infer<(typeof COMMANDS)[T]['payload']>;

export function getCommandDefinition(type: string): CommandDefinition | undefined {
  return (COMMANDS as Record<string, CommandDefinition>)[type];
}
